"use client"

import { useEffect, useState } from "react"
import { AiOutlineCloudDownload, AiOutlineClose } from "react-icons/ai"
import { Button } from "@/components/ui-lab/button"

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

export const PwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch((err) => {
        console.error("SW registration failed:", err)
      })
    }

    const checkIfInstalled = () => {
      const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      const isFullscreen = window.matchMedia("(display-mode: fullscreen)").matches
      const isMinimalUI = window.matchMedia("(display-mode: minimal-ui)").matches
      const isMiniApp = (navigator as Navigator & { standalone?: boolean }).standalone === true

      setIsInstalled(isStandalone || isFullscreen || isMinimalUI || isMiniApp)
    }

    const checkIOSInstall = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      setIsIOS(isIOS)
    }

    checkIfInstalled()
    checkIOSInstall()

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setShowPrompt(true)
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
    }
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return

    try {
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice

      if (outcome === "accepted") {
        setShowPrompt(false)
        setIsInstalled(true)
      }
    } catch (error) {
      console.error("Install prompt error:", error)
    } finally {
      setDeferredPrompt(null)
    }
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    setDeferredPrompt(null)
  }

  if (isInstalled || !showPrompt) return null

  if (isIOS) {
    return (
      <div className="fixed bottom-6 left-4 right-20 sm:left-6 sm:right-auto max-w-sm z-50 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start gap-3">
          <AiOutlineCloudDownload className="text-xl text-blue-500 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <p className="font-semibold text-gray-900 dark:text-white text-sm mb-2">Add to Home Screen</p>
            <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
              Tap Share → Add to Home Screen for quick access
            </p>
            <Button size="sm" onClick={handleDismiss}>
              Got it
            </Button>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
          >
            <AiOutlineClose />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed bottom-6 left-4 right-20 sm:left-6 sm:right-auto max-w-sm z-50 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-3">
        <AiOutlineCloudDownload className="text-xl text-blue-500 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-semibold text-gray-900 dark:text-white text-sm">Install App</p>
          <p className="text-xs text-gray-600 dark:text-gray-300">Quick access & offline mode</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button size="sm" onClick={handleInstall}>
            <AiOutlineCloudDownload />
            Install
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleDismiss} aria-label="Dismiss">
            <AiOutlineClose />
          </Button>
        </div>
      </div>
    </div>
  )
}
